import * as VueRouter from 'vue-router';
import routes from '@/routes/routes'
import sessionService from "@/services/helper/session.service";
import { eTipoPermissao } from '@/enums/ePermissoes';
import { MenuActionTypes } from '@/store/menu/action-types';
import { useStore } from '@/store';
import logService from "@/services/log/log.service";
import toastService from '@/services/globals/toast.service';
import tokenRenewalService from '@/services/auth/token-renewal.service';
import DadosLocais from '@/services/helper/storage';
import anuncioNavegacaoService from '@/services/globals/anuncio-navegacao.service';
import pdiNomenclaturaService from '@/services/educacaoinclusiva/pdires4256/pdinomenclatura.service';

const dadosLocais = DadosLocais.getInstance();

const rotasNovaAba = [
  'dashboard'
];

const router = VueRouter.createRouter({
  history: VueRouter.createWebHistory(),
  routes,
});

const chunkReloadKey = 'router:chunk-reload';

router.onError((error) => {
  const message = error?.message ?? '';
  const isChunkLoadError = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module/i.test(message);

  if (isChunkLoadError && !sessionStorage.getItem(chunkReloadKey)) {
    sessionStorage.setItem(chunkReloadKey, '1');
    window.location.reload();
  }
});

router.afterEach((to) => {
  sessionStorage.removeItem(chunkReloadKey);

  // Título da aba por tela (WCAG 2.4.2) e anúncio da navegação para leitor de tela —
  // em SPA nada disso acontece sozinho. Ver o serviço para o porquê de cada um.
  anuncioNavegacaoService.anunciarTela(to.name, (texto) =>
    pdiNomenclaturaService.aplicarTokens(texto),
  );

  // Widget de acessibilidade (Sienna): visível só fora do app autenticado (landing page, login,
  // recuperação/alteração de senha) — dentro do app ele fica redundante com o ícone do TopBar e
  // só ocupa espaço/sobrepõe outros componentes.
  const dentroDoAppAutenticado = to.matched.some((record) => record.meta?.requiresAuth === true);
  document.body.classList.toggle('oculta-widget-acessibilidade', dentroDoAppAutenticado);
});

router.beforeEach(async (to, from, next) => {
  // Antes era uma whitelist fixa de 4 caminhos (`/`, login, recuperar-senha, alterar-senha), que
  // ignorava `meta.requiresAuth` por completo — qualquer rota nova marcada como pública (ex.:
  // pré-matrícula da Central de Vagas) caía direto no redirect pro login. `to.matched` traz a meta
  // já mesclada do pai pro filho, então uma rota filha com `requiresAuth: false` sobrepõe o
  // `requiresAuth: true` herdado de `/home`.
  const rotaLivreDeSessao = to.matched.some((registro) => registro.meta?.requiresAuth === false);
  const temSessaoAtiva = !!dadosLocais.getCodContrato();

  if (!rotaLivreDeSessao && !temSessaoAtiva) {
    localStorage.removeItem("accessToken");
    toastService.alertaErro('Sessão Expirada! Redirenciondo para o login...');
    next({
      path: "/login"
    });
    return;
  }

  // Checagem proativa de expiração via decode do JWT local (evita perder a corrida contra o
  // interceptor de 401, que só reage DEPOIS de uma requisição falhar) só faz sentido em localhost,
  // onde o token ainda vai pro localStorage (exceção de dev, ver EhOrigemDesenvolvimentoLocal na
  // Kernel API). Em produção (cookie-only, Fase 4) não há token legível no cliente pra decodificar
  // — localStorage.getItem("accessToken") sempre vem null, então tratar isso como "sempre expirado"
  // disparava RenovarToken antes de TODA navegação. A renovação reativa do interceptor de 401 já
  // cobre a expiração real quando uma chamada de fato falhar.
  if (!rotaLivreDeSessao && temSessaoAtiva && location.hostname === 'localhost') {
    const token = localStorage.getItem("accessToken");

    if (tokenExpirado(token)) {
      const renovado = await tentarRenovarTokenAoNavegar();

      if (!renovado) {
        localStorage.removeItem("accessToken");
        toastService.alertaErro('Sessão Expirada! Redirenciondo para o login...');
        next({
          path: "/login"
        });
        return;
      }
    }
  }

  // Corrige também acesso direto e F5 na home padrão de um perfil especializado.
  if (!rotaLivreDeSessao && temSessaoAtiva) {
    const caminhoHome = sessionService.GetCaminhoHome();
    const homes = ['/home', '/homeprofessor', '/homealuno', '/homeresponsavel'];
    if (homes.includes(to.path) && sessionService.IsAlunoOuResponsavelOuProfessor() && to.path !== caminhoHome) {
      next({ path: caminhoHome, replace: true });
      return;
    }
  }

  if (to.name === '404') {
    next();
    return;
  }

  if (to.name) {
    logService.registrarAtividade('TELA_ACESSADA', `Tela acessada: ${to.path}`, {
      rota: to.path,
      modulo: 'Navegacao',
    });
  }

  if (to.name === "conteudolecionado" || to.name === "BNCC" || to.name === "chat2_estudante_professor" || to.name === "chat2_professor_turma" || to.name === "chat2_responsavel_professor") { // Provisório enquanto ainda existe o SAEWEB LEGADO
    next();
    return;
  }

  if (to.name === "chat") {
    next();
    return;
  }

  if (to.name === "chat_professor_turma" && sessionService.IsProfessor()) {
    next();
    return;
  }

  if (to.name === "chat_estudante_professor" && sessionService.IsAluno()) {
    next();
    return;
  }

  if (to.name === "chat_responsavel_professor" && sessionService.IsResponsavel()) {
    next();
    return;
  }

  const podeAcessar = poderAcessarTela(to);

  if (
    rotasNovaAba.includes(String(to.name)) &&
    podeAcessar &&
    window.name !== `saeweb-${String(to.name)}`
  ) {
    const url = router.resolve(to).href;

    window.open(url, `saeweb-${String(to.name)}`);

    next(false);
    return;
  }

  if (podeAcessar) {
    next();
    return;
  }

  next({ name: 'accessdenied' });
});

async function tentarRenovarTokenAoNavegar(): Promise<boolean> {
  try {
    const codigoGrupoUsuarioStr = dadosLocais.getCodigoGrupoUsuario();
    const codigoGrupoUsuario = codigoGrupoUsuarioStr ? Number(codigoGrupoUsuarioStr) : null;

    await tokenRenewalService.renovar(codigoGrupoUsuario);
    return true;
  } catch {
    return false;
  }
}

function tokenExpirado(token: string | null): boolean {
     
  if (!token) return true;

  try {
    const payloadBase64Url = token.split('.')[1];

    if (!payloadBase64Url) return true;

    const payloadBase64 = payloadBase64Url
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(
        payloadBase64Url.length + (4 - payloadBase64Url.length % 4) % 4,
        '='
      );

    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);

     // console.log('Payload JWT:', payload);
     // console.log('Exp:', payload.exp);
     // console.log('Expira em:', new Date(payload.exp * 1000));
     // console.log('Agora:', new Date());

    if (!payload.exp) return true;

    return Date.now() >= payload.exp * 1000;
  } catch (error) {
    console.error('Erro ao validar token:', error);
    return true;
  }
}

function poderAcessarTela(to: VueRouter.RouteLocationNormalized): boolean {
  // Tela não exige permissão ou não foi definida na configuração de sua rota
  if (!to.meta.permissions) {
    return true;
  }

  // Verifica se o usuário tem permissão
  const permissions = to.meta.permissions as eTipoPermissao[];
  const temPermissao = sessionService.TemPermissao(permissions);
  if (temPermissao[0]) {
    const permissaoAcaoTela = sessionService.RetornarPermissaoAcaoTela(temPermissao[1] as eTipoPermissao)
    const readOnly = (permissaoAcaoTela.incluir || permissaoAcaoTela.alterar) ? 0 : 1;
    useStore().dispatch(MenuActionTypes.ACTION_SET_READONLY, readOnly);

    return true;
  }

  // Caso o usuário não tenha permissão, retorna false
  return false;
}

export default router;
